import React from 'react';
import { Card, StatCard, Badge, SectionTitle, Btn } from '../components/UI';
import { format, isPast, parseISO, isToday } from 'date-fns';

function waterStatus(plant) {
  if (!plant.lastWatered || !plant.waterEvery) return null;
  const next = new Date(new Date(plant.lastWatered).getTime() + plant.waterEvery * 86400000);
  const diff = Math.ceil((next - Date.now()) / 86400000);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  return 'ok';
}

export default function Dashboard({ state, setPage }) {
  const { plants, tasks, costs, journalEntries } = state;

  const totalHarvest = plants.reduce((sum, p) => {
    return sum + (p.harvestLog || []).reduce((s, h) => s + (parseFloat(h.amount) || 0), 0);
  }, 0);

  const monthCosts = costs.filter(c => {
    const d = new Date(c.date);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  const pendingTasks = tasks.filter(t => !t.done);
  const needsWater = plants.filter(p => ['overdue', 'today'].includes(waterStatus(p)));
  const recentCosts = [...costs].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 4);

  const today = format(new Date(), 'EEEE d MMMM');

  return (
    <div style={{ padding: '2rem', maxWidth: 900 }}>
      <div style={{ marginBottom: '2rem' }} className="fade-up">
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{today}</div>
        <h1 style={{ fontSize: 28, marginBottom: 4 }}>Kitchen Garden</h1>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
          {pendingTasks.length > 0 ? `${pendingTasks.length} task${pendingTasks.length !== 1 ? 's' : ''} to do` : 'All caught up'}{needsWater.length > 0 ? ` · ${needsWater.length} plant${needsWater.length !== 1 ? 's' : ''} need water` : ''}
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: '1.5rem' }} className="fade-up">
        <StatCard label="Growing" value={plants.length} />
        <StatCard label="Tasks pending" value={pendingTasks.length} />
        <StatCard label="Spent this month" value={`£${monthCosts.toFixed(0)}`} />
        <StatCard label="Harvested" value={totalHarvest.toFixed(1)} unit="kg" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }} className="fade-up">
        <Card>
          <SectionTitle action={<span onClick={() => setPage('plants')} style={{ fontSize: 12, color: 'var(--text-tertiary)', cursor: 'pointer' }}>View all →</span>}>Plants</SectionTitle>
          {plants.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>No plants yet. <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => setPage('plants')}>Add one →</span></p>
          ) : plants.slice(0, 5).map(p => {
            const ws = waterStatus(p);
            return (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                {p.image ? (
                  <img src={p.image} alt={p.name} style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: 6, background: 'var(--moss-50)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>🌿</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{p.variety || p.type || ''}</div>
                </div>
                {ws === 'overdue' && <Badge color="red">Water overdue</Badge>}
                {ws === 'today' && <Badge color="amber">Water today</Badge>}
                {p.status && ws === 'ok' && <Badge color="green">{p.status}</Badge>}
              </div>
            );
          })}
        </Card>

        <Card>
          <SectionTitle action={<span onClick={() => setPage('tasks')} style={{ fontSize: 12, color: 'var(--text-tertiary)', cursor: 'pointer' }}>View all →</span>}>Tasks</SectionTitle>
          {tasks.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>No tasks yet. <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => setPage('tasks')}>Add one →</span></p>
          ) : tasks.slice(0, 5).map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 16, height: 16, borderRadius: 4, border: '1.5px solid var(--border-md)', background: t.done ? 'var(--accent)' : 'transparent', marginTop: 1, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, textDecoration: t.done ? 'line-through' : 'none', color: t.done ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>{t.title}</div>
                {t.assignee && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t.assignee}</div>}
              </div>
              {t.dueDate && !t.done && <div style={{ fontSize: 11, color: isPast(parseISO(t.dueDate)) ? 'var(--red-600)' : 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{format(parseISO(t.dueDate), 'd MMM')}</div>}
            </div>
          ))}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }} className="fade-up">
        <Card>
          <SectionTitle action={<span onClick={() => setPage('costs')} style={{ fontSize: 12, color: 'var(--text-tertiary)', cursor: 'pointer' }}>View all →</span>}>Recent spending</SectionTitle>
          {recentCosts.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>No costs logged yet.</p>
          ) : recentCosts.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontSize: 13 }}>{c.description}</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{c.date ? format(parseISO(c.date), 'd MMM') : ''} · {c.category}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>£{parseFloat(c.amount).toFixed(2)}</div>
            </div>
          ))}
        </Card>

        <Card>
          <SectionTitle>Quick actions</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Btn onClick={() => setPage('plants')} style={{ justifyContent: 'flex-start', width: '100%' }}>🌱 Add a plant</Btn>
            <Btn onClick={() => setPage('tasks')} style={{ justifyContent: 'flex-start', width: '100%' }}>✓ Add a task</Btn>
            <Btn onClick={() => setPage('costs')} style={{ justifyContent: 'flex-start', width: '100%' }}>₤ Log an expense</Btn>
            <Btn onClick={() => setPage('journal')} style={{ justifyContent: 'flex-start', width: '100%' }}>◫ Write a journal entry</Btn>
            <Btn onClick={() => setPage('treemap')} style={{ justifyContent: 'flex-start', width: '100%' }}>⊕ Open tree map</Btn>
          </div>
        </Card>
      </div>
    </div>
  );
}
